// Premium App Showcase - Gallery treatment with signature visual DNA
import React, { useState } from 'react';
import { Smartphone, Monitor, Zap, Settings, ArrowRight, Globe, Code, Rocket } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import { MicroLabel } from './micro-label';

const showcaseItems = [
  {
    title: 'Web Applications',
    description: 'Custom web apps that streamline operations and scale with your business',
    icon: Code,
    signature: 'grid', // Geometric grid pattern
    features: [
      'Scalable cloud architecture',
      'Real-time data synchronization', 
      'Custom integrations',
      'Mobile-responsive design'
    ]
  },
  {
    title: 'Mobile Apps (iOS/Android)',
    description: 'Native mobile apps with offline-first architecture',
    icon: Smartphone,
    signature: 'dots', // App grid dots pattern
    features: [
      'Native iOS and Android development',
      'Offline-first architecture',
      'Push notifications',
      'App Store optimization'
    ]
  },
  {
    title: 'SaaS MVPs',
    description: 'Launch your SaaS idea with proven development process',
    icon: Rocket,
    signature: 'bar', // Progress indicators
    features: [
      'Rapid MVP development',
      'Built-in user authentication',
      'Payment processing integration',
      'Analytics and reporting'
    ]
  },
  {
    title: 'Website-as-a-Service',
    description: 'Professional websites with hosting, updates, and growth handled for you',
    icon: Globe,
    signature: 'notch', // Globe with upward arrow
    features: [
      'Hosting + SSL included',
      'Monthly content updates',
      'SEO optimization',
      'Mobile-responsive design'
    ]
  }
];

export const AppShowcase = () => {
  const [activeCategory, setActiveCategory] = useState(0);
  const { elementRef, getRevealStyle } = useScrollReveal();

  return (
    <section className="section">
      <div className="container-professional">
        {/* Header */}
        <div className="section-header-professional">
          <div className="micro-label-professional">WHAT WE BUILD</div>
          <h2 className="heading-section">
            Products that drive measurable results
          </h2>
          <p className="text-body max-w-2xl mx-auto" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Web apps, mobile apps, and websites built for scale and performance
          </p>
        </div>

        {/* Clean 4-column grid */}
        <div ref={elementRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {showcaseItems.map((item, index) => {
            const Icon = item.icon;
            const isActive = activeCategory === index;
            
            return (
              <div 
                key={index}
                className="group cursor-pointer"
                style={getRevealStyle(index)}
                onClick={() => setActiveCategory(index)}
                onMouseEnter={() => setActiveCategory(index)}
              >
                <div 
                  className="bg-[#111111] border rounded-xl p-6 h-full flex flex-col transition-all duration-200 hover:transform hover:-translate-y-1"
                  style={{
                    borderColor: isActive ? 'rgba(0, 229, 255, 0.4)' : 'rgba(255, 255, 255, 0.15)',
                  }}
                >
                  {/* Icon */}
                  <Icon 
                    className="w-8 h-8 mb-4" 
                    strokeWidth={1.5}
                    style={{ color: '#00e5ff' }}
                  />
                  
                  {/* Title */}
                  <h3 className="text-lg font-semibold text-white mb-2 leading-tight">
                    {item.title}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-sm leading-relaxed mb-4" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {item.description}
                  </p>
                  
                  {/* Features preview */}
                  <div className="space-y-2 mt-auto">
                    {item.features.slice(0, 2).map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <div className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ background: '#00e5ff' }}></div>
                        <span className="text-xs leading-relaxed" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Detailed view section */}
        <div className="mt-20 max-w-7xl mx-auto">
          <div 
            className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden backdrop-blur-sm"
            style={getRevealStyle(5)}
          >
            <div>
              
              {/* Content */}
            <div className="p-12 md:p-16 lg:p-20">
                <div className="inline-block px-3 py-1 rounded-full bg-[#00e5ff]/10 border border-[#00e5ff]/20 mb-6">
                  <span className="text-xs font-medium tracking-wide" style={{ color: '#00e5ff' }}>
                    SELECTED SERVICE
                  </span>
                </div>
                
                <h3 className="text-3xl md:text-4xl font-semibold text-white mb-5 leading-tight">
                  {showcaseItems[activeCategory].title}
                </h3>
                
                <p className="text-base md:text-lg leading-relaxed mb-10 text-white/70">
                  {showcaseItems[activeCategory].description}
                </p>

                {/* Full feature list */}
                <div className="space-y-4 mb-12">
                  {showcaseItems[activeCategory].features.map((feature, index) => (
                    <div 
                      key={index} 
                      className="flex items-start gap-3.5 group"
                    >
                      <div className="w-5 h-5 rounded-full border border-[#00e5ff]/30 bg-[#00e5ff]/5 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all duration-200 group-hover:border-[#00e5ff]/60 group-hover:bg-[#00e5ff]/10">
                        <div className="w-2 h-2 rounded-full bg-[#00e5ff]"></div>
                      </div>
                      <span className="text-sm md:text-base leading-relaxed text-white/80">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
                
                {/* CTA */}
                <div>
                  {showcaseItems[activeCategory].title === 'Website-as-a-Service' ? (
                    <Link to="/pricing">
                      <button 
                        className="inline-flex items-center gap-2.5 bg-[#00e5ff] text-black font-semibold text-sm px-8 py-3.5 rounded-lg transition-all duration-200 hover:bg-[#00d4e6] hover:transform hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(0,229,255,0.3)]"
                      >
                        See pricing plans
                        <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                      </button>
                    </Link>
                  ) : (
                    <Link to="/contact">
                      <button 
                        className="inline-flex items-center gap-2.5 bg-[#00e5ff] text-black font-semibold text-sm px-8 py-3.5 rounded-lg transition-all duration-200 hover:bg-[#00d4e6] hover:transform hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(0,229,255,0.3)]"
                      >
                        Request quote
                        <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                      </button>
                    </Link>
                  )}
                </div>
              </div>
              
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};